/**
 * Automation Builder — create/edit automation with trigger + action pipeline.
 */

import { useState } from 'react'
import { Plus, Trash2, ArrowDown } from 'lucide-react'
import { useCRM } from '@/hooks/useCRM'
import { TRIGGER_TYPES, ACTION_TYPES } from '@/types/crm'
import type { AutomationTrigger, AutomationAction } from '@/types/crm'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useToast,
} from '@/components/ui'

interface AutomationBuilderProps {
  onClose: () => void
}

export function AutomationBuilder({ onClose }: AutomationBuilderProps) {
  const { createAutomation } = useCRM()
  const { addToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [trigger, setTrigger] = useState<AutomationTrigger>({ type: 'contact_created' })
  const [actions, setActions] = useState<AutomationAction[]>([
    { type: 'create_activity', config: { activity_type: 'task', title: '', due_days: 1 } },
  ])

  const addAction = () => {
    setActions([...actions, { type: 'add_tag', config: { tag: '' } }])
  }

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index))
  }

  const updateAction = (index: number, updates: Partial<AutomationAction>) => {
    const newActions = [...actions]
    newActions[index] = { ...newActions[index], ...updates }
    setActions(newActions)
  }

  const handleSubmit = async () => {
    if (!name.trim() || actions.length === 0) return
    setIsSubmitting(true)
    const result = await createAutomation({
      name: name.trim(),
      description: description.trim() || undefined,
      trigger,
      actions,
      is_active: false,
    })
    setIsSubmitting(false)
    if (result) {
      addToast({ type: 'success', title: 'Automation created' })
      onClose()
    } else {
      addToast({ type: 'error', title: 'Failed to create automation', description: 'Check your connection and try again.' })
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent
        size="md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <ModalHeader><ModalTitle>New Automation</ModalTitle></ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <ModalBody className="space-y-4">
          <Input
            label="Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Post-delivery feedback"
            autoFocus
          />

          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this automation do?"
          />

          {/* Trigger */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">When</label>
            <div className="p-3 bg-info/10 border border-info/20 rounded-lg">
              <Select value={trigger.type} onValueChange={(v) => setTrigger({ type: v, conditions: {} })}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {trigger.type === 'order_status_changed' && (
                <Input
                  placeholder="Status (e.g., delivered)"
                  value={(trigger.conditions?.status as string) || ''}
                  onChange={(e) => setTrigger({ ...trigger, conditions: { ...trigger.conditions, status: e.target.value } })}
                  className="mt-2"
                />
              )}
              {trigger.type === 'contact_tag_added' && (
                <Input
                  placeholder="Tag name"
                  value={(trigger.conditions?.tag as string) || ''}
                  onChange={(e) => setTrigger({ ...trigger, conditions: { ...trigger.conditions, tag: e.target.value } })}
                  className="mt-2"
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Then</label>
            <div className="space-y-2">
              {actions.map((action, index) => (
                <div key={index}>
                  {index > 0 && (
                    <div className="flex justify-center py-1">
                      <ArrowDown className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <ActionRow
                    action={action}
                    onChange={(updates) => updateAction(index, updates)}
                    onRemove={() => removeAction(index)}
                    canRemove={actions.length > 1}
                  />
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addAction} className="mt-2">
              <Plus className="w-3.5 h-3.5" />Add step
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || actions.length === 0} isLoading={isSubmitting}>
            Save Automation
          </Button>
        </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

// ── Action Row ──────────────────────────────────────────────────────────────

function ActionRow({ action, onChange, onRemove, canRemove }: {
  action: AutomationAction
  onChange: (updates: Partial<AutomationAction>) => void
  onRemove: () => void
  canRemove: boolean
}) {
  const updateConfig = (key: string, value: unknown) => {
    onChange({ config: { ...action.config, [key]: value } })
  }

  return (
    <div className="p-3 bg-muted border border-border rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        <Select value={action.type} onValueChange={(v) => onChange({ type: v, config: {} })}>
          <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canRemove && (
          <Button type="button" variant="outline" size="sm" aria-label="Remove step" onClick={onRemove}>
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </Button>
        )}
      </div>

      {action.type === 'create_activity' && (
        <div className="space-y-2">
          <Input
            placeholder="Task title"
            value={(action.config.title as string) || ''}
            onChange={(e) => updateConfig('title', e.target.value)}
          />
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Due in (days)"
                min="0"
                value={(action.config.due_days as number) || ''}
                onChange={(e) => updateConfig('due_days', parseInt(e.target.value) || 1)}
              />
            </div>
            <div className="flex-1">
              <Select
                value={(action.config.priority as string) || 'normal'}
                onValueChange={(v) => updateConfig('priority', v)}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {(action.type === 'add_tag' || action.type === 'remove_tag') && (
        <Input
          placeholder="Tag name"
          value={(action.config.tag as string) || ''}
          onChange={(e) => updateConfig('tag', e.target.value)}
        />
      )}
    </div>
  )
}
